/**
 * NeedsAttentionCard — the Dashboard's top-3 OPEN notifications feed
 * (WSRPDM-0017, contract §4.1). The parent (`Dashboard.tsx`) already fetches
 * `listNotifications({ status: 'OPEN', size: 3 })`, so this just renders
 * whatever it's given (`.slice(0, 3)` defensively) — one line per OPEN row
 * (pill, title, optional slug, date, acknowledge). Acknowledge POSTs the
 * existing notifications endpoint and refetches the OPEN feed so a success
 * drops the row (WSRPDM-0076). "View all notifications" lives on the parent
 * Paper header, not here.
 *
 * Localizes the title off `type` (never `message`), same as
 * `NotificationRow` — `notifType.<type>.title`, raw-enum fallback via
 * `notificationTypeLabel` for an unmapped type.
 */
import { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { Alert, IconButton, Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { acknowledge } from '@/api/wsr/notifications';
import { wsrApiError } from '@/api/wsr/errors';
import { notificationTypeLabel } from '@/domain/registry';
import { SeverityPill } from '@/components/ui';
import type { PlatformNotificationView } from '@/api/wsr/types';

interface NeedsAttentionCardProps {
  notifications: PlatformNotificationView[];
  refetch: () => void;
}

export function NeedsAttentionCard({ notifications, refetch }: NeedsAttentionCardProps) {
  const { t } = useCopy();
  const rows = notifications.slice(0, 3);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [ackError, setAckError] = useState<string | null>(null);

  const handleAcknowledge = async (id: number) => {
    setAckError(null);
    setBusyId(id);
    try {
      await acknowledge(id);
      refetch();
    } catch (err) {
      const wsrErr = wsrApiError(err);
      setAckError(
        wsrErr?.message ??
          t('notifications.ackError', 'Could not acknowledge this notification. Please try again.'),
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={1.5}>
      {ackError ? <Alert severity="error">{ackError}</Alert> : null}
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.needsAttention.empty', 'No open notifications.')}
        </Typography>
      ) : (
        <Stack spacing={1.5} aria-label={t('dashboard.needsAttention.title', 'Needs attention')}>
          {rows.map((notification) => {
            const title = t(
              `notifType.${notification.type}.title`,
              notificationTypeLabel(notification.type, t),
            );
            return (
              <Stack
                key={notification.id}
                direction="row"
                spacing={0.75}
                sx={{ alignItems: 'center', minWidth: 0 }}
              >
                <SeverityPill severity={notification.severity} />
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {title}
                </Typography>
                {notification.tenantSlug !== '*' ? (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {notification.tenantSlug}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary" noWrap>
                  {new Date(notification.lastSeenAt).toLocaleString()}
                </Typography>
                {notification.status === 'OPEN' ? (
                  <IconButton
                    type="button"
                    size="small"
                    aria-label={t('notifications.actions.acknowledge', 'Acknowledge')}
                    disabled={busyId === notification.id}
                    onClick={() => void handleAcknowledge(notification.id)}
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
